import { createFileRoute } from "@tanstack/react-router";
import PosicionamientoWeb from "@/pages/PosicionamientoWeb";
import { buildBreadcrumb, ldJson } from "@/lib/site-schema";

const canonicalUrl = "https://agenciaseoevolution.com/posicionamiento-web";

const faqs = [
  {
    question: "¿Qué es el posicionamiento web y para qué sirve?",
    answer:
      "El posicionamiento web —también llamado SEO (Search Engine Optimization)— es el conjunto de técnicas que sitúan una web en las primeras posiciones de Google y de los motores generativos (ChatGPT, Perplexity, Gemini, Atlas) para las búsquedas comerciales de su sector. Sirve para captar tráfico cualificado de forma sostenida, sin depender de la publicidad de pago, y convertirlo en clientes y facturación.",
  },
  {
    question: "¿Cuánto cuesta el posicionamiento web profesional en España?",
    answer:
      "Depende del mercado, la competencia del sector (legal, salud, ecommerce o inmobiliario exigen más trabajo), el alcance y los objetivos, con compromiso mínimo de 6 meses. No publicamos tarifas: cada empresa necesita una estrategia propia y la propuesta se concreta tras un diagnóstico técnico real.",
  },
  {
    question: "¿En cuánto tiempo se ven resultados de posicionamiento web?",
    answer:
      "Los primeros movimientos en SERPs aparecen entre la semana 8 y la 14, según competencia del sector y estado técnico de partida de la web. Resultados consolidados —tráfico orgánico recurrente y leads cualificados— requieren entre 5 y 9 meses. Para keywords muy competidas planificamos 9-15 meses. Por eso exigimos compromiso mínimo de 6 meses: el SEO sin tiempo no funciona, da igual la agencia.",
  },
  {
    question: "¿Qué diferencia hay entre SEO y posicionamiento web?",
    answer:
      "Ninguna técnica. Son sinónimos: SEO (Search Engine Optimization) y posicionamiento web se refieren al mismo trabajo —optimizar una web para que aparezca en las primeras posiciones de Google de forma orgánica—. La diferencia es solo de nomenclatura: \"SEO\" es el término internacional; \"posicionamiento web\" es la traducción habitual en España.",
  },
  {
    question: "¿Qué incluye un servicio profesional de posicionamiento web?",
    answer:
      "Un servicio profesional incluye, como mínimo: (1) auditoría técnica completa —Core Web Vitals, indexación, schema, canibalización, arquitectura—; (2) keyword research por intención de búsqueda y por sector; (3) optimización on-page (etiquetas, contenido, estructura); (4) creación o reescritura de contenidos de autoridad optimizados para Google y motores generativos; (5) link building con medios autoritativos; (6) seguimiento técnico continuo y reporting mensual de facturación.",
  },
  {
    question: "¿Garantizáis aparecer en el #1 de Google?",
    answer:
      "No. Cualquier agencia que garantice posiciones está mintiendo o usando técnicas de riesgo que terminan en penalización de Google. Lo que sí garantizamos es ejecutar el trabajo profesional que las primeras posiciones requieren: auditoría completa, contenido de autoridad, link building de calidad, optimización técnica continua y reporting transparente. Las posiciones llegan como consecuencia del trabajo bien hecho, no como promesa de venta.",
  },
];
const businessSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${canonicalUrl}#service`,
  name: "Posicionamiento Web profesional con IA",
  serviceType: "SEO / Posicionamiento Web",
  provider: {
    "@type": "Organization",
    name: "SEOEVOLUTION",
    url: "https://agenciaseoevolution.com",
  },
  areaServed: { "@type": "Country", name: "España" },
  description:
    "Posicionamiento web profesional con IA en España. Auditoría técnica, contenidos de autoridad, link building premium y reporting de facturación. Propuesta personalizada tras diagnóstico.",
  url: canonicalUrl,
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const breadcrumbSchema = buildBreadcrumb([{ name: "Posicionamiento web", path: "/posicionamiento-web" }]);

export const Route = createFileRoute("/posicionamiento-web")({
  head: () => ({
    meta: [
      { title: "Posicionamiento Web profesional con IA en España | SEOEVOLUTION" },
      {
        name: "description",
        content:
          "Posicionamiento web profesional con IA en España. Auditoría técnica, contenidos de autoridad y link building premium. Compromiso mínimo 6 meses. Propuesta personalizada tras diagnóstico.",
      },
      {
        name: "keywords",
        content:
          "posicionamiento web, posicionamiento web profesional, posicionamiento web España, posicionamiento SEO, posicionamiento orgánico, agencia posicionamiento web",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalUrl },
      { property: "og:title", content: "Posicionamiento Web profesional con IA en España | SEOEVOLUTION" },
      {
        property: "og:description",
        content:
          "Trabajo profesional para competir por las primeras posiciones de Google. Sin promesas de #1. Reporting de facturación, no de impresiones.",
      },
      { name: "twitter:title", content: "Posicionamiento Web profesional con IA en España | SEOEVOLUTION" },
      {
        name: "twitter:description",
        content:
          "Trabajo profesional para competir por las primeras posiciones de Google. Sin promesas de #1.",
      },
    ],
    links: [{ rel: "canonical", href: canonicalUrl }],
    scripts: [
      ldJson(breadcrumbSchema),
      { type: "application/ld+json", children: JSON.stringify(businessSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
    ],
  }),
  component: PosicionamientoWeb,
});
